import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useStore } from '../../stores/root-store-context';
import type { Project } from '../../stores/ProjectsStore';
import { ProjectProvider } from './ProjectContext';
import type { CurrentProject } from './ProjectContext';

export type ProjectLoadState = CurrentProject & {
  /** `getBySlug` resolved to nothing: unknown slug or id. */
  notFound: boolean;
  /** `getBySlug` rejected. */
  loadError: unknown;
};

/**
 * Resolves the project named by a route identifier (slug or numeric id) exactly once per
 * identifier through `projectsStore.getBySlug`. `reload()` re-reads it with `forceLoad`.
 * Lives apart from `ProjectContext.tsx` so the context module stays free of store imports.
 */
export function useProjectLoader(identifier: string | undefined): ProjectLoadState {
  const { projectsStore } = useStore();
  const [project, setProject] = useState<Project | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(!!identifier);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);

  useEffect(() => {
    if (!identifier) {
      setProject(undefined);
      setIsLoading(false);
      setNotFound(false);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    setLoadError(null);
    projectsStore
      .getBySlug(identifier)
      .then((loaded) => {
        if (cancelled) return;
        setProject(loaded);
        setNotFound(!loaded);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to load project:', error);
        setProject(undefined);
        setLoadError(error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [identifier, projectsStore]);

  const reload = useCallback(async () => {
    if (!identifier) return undefined;
    const loaded = await projectsStore.getBySlug(identifier, true);
    setProject(loaded);
    setNotFound(!loaded);
    return loaded;
  }, [identifier, projectsStore]);

  return useMemo(
    () => ({
      project,
      projectId: project ? String(project.id) : undefined,
      isLoading,
      isAnonymous: false,
      reload,
      notFound,
      loadError,
    }),
    [project, isLoading, reload, notFound, loadError],
  );
}

/**
 * Provides the current project for a route that has no `ProjectContainerLayout` (the settings
 * page renders its own `Layout`). Reads the same route params the layout does.
 */
export const ProjectRouteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { projectSlug, projectId, id } = useParams<{ projectSlug?: string; projectId?: string; id?: string }>();
  const state = useProjectLoader(projectSlug || projectId || id);
  return <ProjectProvider value={state}>{children}</ProjectProvider>;
};
